package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic

/**
 * 单选组容器。
 *
 * 布局上等同于 [Column]（子项垂直排列）。负责维护 selectedValue：
 * 当通过 [child] 添加 [RadioButton] 时，给其注入 onSelectRequest 回调，
 * 子项被点击 → 回调 [selectedValue] → 同步所有子 RadioButton 的 selected 状态并触发 onChange。
 */
class RadioGroup private constructor() : UIElement<RadioGroup>() {

    // 字段名与同名访问器方法冲突，backing 改名加后缀
    private var selectedValueText: String = ""
    private var onChangeHandler: UiEventHandler<ChangeEvent<String>>? = null
    private var boundSelectedValueSignal: Signal<in String>? = null

    init {
        // 默认垂直排列；如需横向可在 DSL 通过 layout 属性覆盖（当前 PropAppliers 无对应键，保持简单）
        styleField.layout().mode = LayoutMode.COLUMN
    }

    override fun self(): RadioGroup = this

    override fun child(child: UIElement<*>): RadioGroup {
        super.child(child)
        // 关键接线：RadioButton 子项被点击时通知本 group 切换 selectedValue
        if (child is RadioButton) {
            child.onSelectRequest { selectedValue(child.groupValue()) }
            // 添加时同步当前 selected 状态
            child.selected(selectedValueText == child.groupValue())
        }
        return this
    }

    fun selectedValue(v: String?): RadioGroup {
        val n = v ?: ""
        val old = selectedValueText
        applySelectedValue(n)
        // 仅当值真正变化时回写绑定信号并派发 onChange；null 目标由安全调用直接跳过，无需显式判空
        if (old != n) {
            boundSelectedValueSignal?.set(n)
            onChangeHandler?.accept(ChangeEvent(old, n))
        }
        return this
    }

    fun selectedValue(): String = selectedValueText

    fun onChange(handler: UiEventHandler<ChangeEvent<String>>?): RadioGroup {
        onChangeHandler = handler
        return this
    }

    /** 双向绑定到响应式 Signal。 */
    fun bindSelectedValue(signal: Signal<in String>): RadioGroup {
        // 逆变投影 Signal<in String>：接收任意可写入 String 的信号，写入位放开可直接 set(String)，
        // 读取位投影为 Any? 交由 toStringValue 解码——无需强转。
        boundSelectedValueSignal = signal
        lifecycleScopeField.register(Effect { applySelectedValue(toStringValue(signal.get())) })
        return this
    }

    private fun applySelectedValue(value: String) {
        selectedValueText = value
        // 同步子 RadioButton 的 selected 状态
        for (c in childrenField) {
            if (c is RadioButton) {
                c.selected(value == c.groupValue())
            }
        }
    }

    companion object {
        @JvmStatic
        fun create(): RadioGroup = RadioGroup()

        private fun toStringValue(v: Any?): String = v?.toString() ?: ""
    }
}
